import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Typography,
  CircularProgress,
  TablePagination,
} from '@material-ui/core';
import PaginationData from 'types/models/PaginationData';
import { StockMovement } from 'types/models/StockMovement';
import {
  getPendingWarehouseMovements,
  acceptStockMovement,
  cancelStockMovement,
} from 'services/stockMovementsServices';
import AppContext from 'contexts/AppContext';
import { getApiErrorMessage } from 'utils/errors';
import { formatDateTime } from 'utils/datetime';
import ConfirmDialog from 'components/ConfirmDialog';

interface Props {
  warehouseId?: string;
}

const PendingWarehouseMovements: React.FC<Props> = ({ warehouseId }) => {
  const { t } = useTranslation();
  const { authData } = React.useContext(AppContext);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);
  const [movements, setMovements] = React.useState<PaginationData<StockMovement>>();
  const [page, setPage] = React.useState<number>(0);
  const [dialogMovementId, setDialogMovementId] = React.useState<string | null>(null);
  const [dialogCancelMovementId, setDialogCancelMovementId] = React.useState<string | null>(null);
  const [accepting, setAccepting] = React.useState<boolean>(false);
  const [cancelling, setCancelling] = React.useState<boolean>(false);

  const load = React.useCallback(
    async (currentPage: number) => {
      if (!warehouseId) return;
      setLoading(true);
      setError(null);
      try {
        const data = await getPendingWarehouseMovements(warehouseId, currentPage + 1);
        setMovements(data);
      } catch (e: any) {
        setError(e?.response?.data?.message ?? t('stock.movements.pending.error-loading'));
      } finally {
        setLoading(false);
      }
    },
    [warehouseId, t],
  );

  React.useEffect(() => {
    if (warehouseId) {
      setPage(0);
      load(0);
    } else {
      setMovements(undefined);
    }
  }, [warehouseId, load]);

  const handleAccept = async (movementId: string) => {
    if (!authData?.user?.id) return;
    setError(null);
    setAccepting(true);
    try {
      await acceptStockMovement(movementId, authData.user.id);
      load(page);
      window.dispatchEvent(new CustomEvent('pending-movements-changed'));
    } catch (error: any) {
      setError(getApiErrorMessage(error) ?? t('errors.generic'));
    } finally {
      setAccepting(false);
      setDialogMovementId(null);
    }
  };

  const handleCancel = async (movementId: string) => {
    if (!authData?.user?.id) return;
    setError(null);
    setCancelling(true);
    try {
      await cancelStockMovement(movementId, authData.user.id);
      load(page);
      window.dispatchEvent(new CustomEvent('pending-movements-changed'));
    } catch (error: any) {
      setError(getApiErrorMessage(error) ?? t('errors.generic'));
    } finally {
      setCancelling(false);
      setDialogCancelMovementId(null);
    }
  };

  if (!warehouseId) return null;

  return (
    <Paper style={{ padding: 16, marginTop: 16, marginBottom: 16 }}>
      <Typography variant="h6">{t('stock.movements.pending.title')}</Typography>

      {loading && <CircularProgress size={24} style={{ margin: '12px 0' }} />}
      {error && (
        <Typography color="error" style={{ margin: '8px 0' }}>
          {error}
        </Typography>
      )}

      {!loading && movements && movements.data.length === 0 && (
        <Typography style={{ margin: '8px 0' }}>{t('stock.movements.pending.empty')}</Typography>
      )}

      {movements && movements.data.length > 0 && (
        <>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('stock.movements.pending.table.source')}</TableCell>
                  <TableCell>{t('stock.movements.pending.table.products')}</TableCell>
                  <TableCell>{t('stock.movements.pending.table.created-at')}</TableCell>
                  <TableCell align="right">{t('stock.movements.pending.table.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {movements.data.map((movement) => (
                  <TableRow key={movement.id}>
                    <TableCell>{movement.source?.name ?? '-'}</TableCell>
                    <TableCell>
                      {movement.items
                        ?.map((item) => `${item.globalProduct?.name ?? ''} (${item.quantity})`)
                        .join(', ') ?? '-'}
                    </TableCell>
                    <TableCell>{formatDateTime(movement.localDatetime ?? movement.createdAt)}</TableCell>
                    <TableCell align="right">
                      <Button
                        color="primary"
                        variant="contained"
                        disabled={accepting || cancelling}
                        onClick={() => setDialogMovementId(movement.id)}
                        style={{ marginRight: 8 }}
                      >
                        {t('stock.movements.pending.accept')}
                      </Button>
                      <Button
                        color="secondary"
                        variant="outlined"
                        disabled={accepting || cancelling}
                        onClick={() => setDialogCancelMovementId(movement.id)}
                      >
                        {t('stock.movements.pending.cancel')}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={movements.total}
            page={page}
            onPageChange={(_, newPage) => {
              setPage(newPage);
              load(newPage);
            }}
            rowsPerPage={movements.perPage}
            rowsPerPageOptions={[movements.perPage]}
          />
        </>
      )}

      <ConfirmDialog
        open={dialogMovementId !== null}
        setOpen={(open) => {
          if (!open) setDialogMovementId(null);
        }}
        title={t('stock.movements.pending.confirm-title')}
        description={t('stock.movements.pending.confirm-description')}
        onConfirm={() => dialogMovementId && handleAccept(dialogMovementId)}
        onCancel={() => setDialogMovementId(null)}
      />

      <ConfirmDialog
        open={dialogCancelMovementId !== null}
        setOpen={(open) => {
          if (!open) setDialogCancelMovementId(null);
        }}
        title={t('stock.movements.pending.cancel-title')}
        description={t('stock.movements.pending.cancel-description')}
        onConfirm={() => dialogCancelMovementId && handleCancel(dialogCancelMovementId)}
        onCancel={() => setDialogCancelMovementId(null)}
      />
    </Paper>
  );
};

export default PendingWarehouseMovements;
